import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useContext, useEffect } from "react";
import {
  setAutomationPath,
  setAutomationType,
  setFeatureFiles,
  setTestCasesState,
} from "../../../../../../../../../../../store/slices/test-automation/web/flow-stages/playwrightCSharpSpecflowSlices";
import { useNavigateWithSameKey } from "../../../../../../../../../../../custom-hooks/navigateWithKey";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";
import TestCasesImportedPage from "../../../../../shared/TestCasesImportedPage";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../../../../../../../../ServicePage";
import { featureFilesWithToolCalling } from "../../../../../../../../../../../services/webServices";
import { handleProgressBar } from "../../../../../../../../../../custom-components/ProgressBar/ProgressBarWithAPI";

const TestCasesImportedPagePlaywrightCSharpSpecflow = () => {
  const location = useLocation();
  const navigate = useNavigateWithSameKey();
  const dispatch = useDispatch();

  const { automationType, automationPath, locationKey } = location.state;

  const { testCases, isLoading, error } = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "playwrightCSharpSpecflow"
      ] || {}
  );

  const { setLoadingState } = useContext(LoadingModalContext);
  const { setMessage, setMessagePopup } = useContext(MessagePopupContext);

  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }

    if ((!testCases || testCases.length === 0) && !!location.state?.testCases) {
      dispatch(setTestCasesState(locationKey, location.state.testCases));
      dispatch(setAutomationType(locationKey, automationType));
      dispatch(setAutomationPath(locationKey, automationPath));
    }
  }, [location, testCases]);

  const defaultHandleBack = () => {
    navigate(-1);
  };

  const handleContinueClick = async () => {
    try {
      let progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 18000,
      });
      progressHandler.start("Generating feature file...");
      const generateFeatureFileWithToolCalling =
        await featureFilesWithToolCalling({ testCases });
        // Store in Redux
        dispatch(setFeatureFiles(locationKey, generateFeatureFileWithToolCalling.data));
        // Simple navigation without passing state
        navigate(`${automationPath}/feature-file`);
        await progressHandler.finishAndClose();
        progressHandler.stop();
    } catch (error) {
      setMessage(error.response?.data?.message || "Couldn't process your request please try again");
      setMessagePopup(true);
    } finally {
      setLoadingState({
        open: false,
        message: "",
        progress: null,
      });
    }
  };

  return (
    <div>
      {isLoading && (
        <Container maxWidth="md">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="200px"
            textAlign="center"
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1">Loading test cases...</Typography>
          </Box>
        </Container>
      )}

      {!!error && (
        <Container maxWidth="md">
          <Box sx={{ py: 3 }}>
            <Alert severity="error">
              <Typography variant="body1">
                <strong>Error:</strong> {error}
              </Typography>
            </Alert>
          </Box>
        </Container>
      )}

      {(!testCases || testCases.length === 0) && (
        <Box
          sx={{
            background: "#F6F3F2",
            display: "flex",
            flexDirection: "column",
            py: 4,
          }}
        >
          <Container sx={{ width: "100%", flex: 1 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                mb: 4,
                fontWeight: "bold",
                color: "#333",
                fontSize: "28px",
                fontFamily: "Manrope",
              }}
            >
              Test Case Imported
            </Typography>
            <Card
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                mb: 4,
                maxWidth: "100%",
              }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#666", fontFamily: "Manrope", mb: 2 }}
                >
                  No test cases available. Please go back and upload test cases
                  first.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<BackIcon />}
                  onClick={defaultHandleBack}
                  sx={{
                    mt: 2,
                    bgcolor: "#0094FB",
                    textTransform: "none",
                    fontFamily: "Manrope",
                    "&:hover": { bgcolor: "#0078D4" },
                  }}
                >
                  Go Back
                </Button>
              </CardContent>
            </Card>
          </Container>
        </Box>
      )}

      {!!testCases && testCases.length > 0 && (
        <Box sx={{ mt: 4 }}>
          {/*Common Test Cases Imported Page */}
          <TestCasesImportedPage testCases={testCases} />

          {/* Footer Navigation */}
          <Box
            sx={{
              p: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "transparent",
              borderRadius: "0 0 8px 8px",
            }}
          >
            {/* Back Button */}
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={defaultHandleBack}
              sx={{
                // width: "120px",
                // height: "40px",
                borderColor: "#0094FB",
                color: "#0094FB",
                textTransform: "none",
                // fontFamily: "Manrope",
                fontWeight: 500,
                borderRadius: "8px",
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                // gap: "8px",
                "&:hover": {
                  borderColor: "#0094FB",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Back
            </Button>

            {/* Continue Button */}
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleContinueClick}
              disabled={!testCases || testCases.length === 0}
              sx={{
                // width: "199px",
                // height: "40px",
                // fontFamily: "Manrope",
                // gap: "8px",
                backgroundColor: "#0094FB",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                borderWidth: "2px",
                borderColor: "#0094FB",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                "&:hover": {
                  backgroundColor: "#0078D4",
                  borderColor: "#0078D4",
                },
              }}
            >
              Generate Feature File
            </Button>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default TestCasesImportedPagePlaywrightCSharpSpecflow;
